import { Button } from "@heroui/react";
import { useMemo, useState } from "react";
import type { ConflictDetail } from "@/api/accounting/conflicts";
import { buildDiffRows } from "./diffRows";

function formatValue(value: unknown) {
  if (value === undefined) return "—";
  if (value === null) return "null";
  if (typeof value === "object") return JSON.stringify(value);
  return String(value);
}

function DiffCell({
  value,
  changed,
  side,
}: {
  value: unknown;
  changed: boolean;
  side: "domain" | "accounting";
}) {
  const background = changed
    ? side === "domain"
      ? "bg-danger-50 dark:bg-danger-100/20"
      : "bg-success-50 dark:bg-success-100/20"
    : "";

  return (
    <div className={`px-2 py-1 break-all ${background}`}>
      {formatValue(value)}
    </div>
  );
}

export function ConflictDiff({ detail }: { detail: ConflictDetail }) {
  const [showAll, setShowAll] = useState(false);

  const rows = useMemo(
    () =>
      buildDiffRows(
        detail.diff,
        detail.domainSnapshot,
        detail.accountingSnapshot,
      ),
    [detail],
  );

  const hiddenCount = rows.filter((row) => !row.changed).length;
  const visibleRows = showAll ? rows : rows.filter((row) => row.changed);

  if (rows.length === 0) {
    return <span className="text-default-400">Нет данных для сравнения</span>;
  }

  return (
    <div className="flex flex-col gap-2">
      <div className="grid grid-cols-[minmax(0,1fr)_minmax(0,1fr)] gap-x-4 text-small">
        <div className="font-medium pb-1 border-b-2 border-foreground-200">
          Elastic
        </div>
        <div className="font-medium pb-1 border-b-2 border-foreground-200">
          1С
        </div>
        {visibleRows.map((row) => (
          <div key={row.field} className="contents">
            <div className="border-b border-foreground-100 py-1">
              <div className="text-tiny text-default-400 px-2">{row.field}</div>
              <DiffCell
                value={row.domainValue}
                changed={row.changed}
                side="domain"
              />
            </div>
            <div className="border-b border-foreground-100 py-1">
              <div className="text-tiny text-default-400 px-2">{row.field}</div>
              <DiffCell
                value={row.accountingValue}
                changed={row.changed}
                side="accounting"
              />
            </div>
          </div>
        ))}
      </div>
      {hiddenCount > 0 && (
        <Button
          size="sm"
          variant="light"
          className="max-w-fit"
          onPress={() => setShowAll(!showAll)}>
          {showAll
            ? "Скрыть совпадающие поля"
            : `Показать все поля (ещё ${hiddenCount})`}
        </Button>
      )}
    </div>
  );
}
